// Durable case-request ledger API client (case-request-ledger, 2026-10-21).
//
// Server-side, durable request rows for the case chat + doc-drop UI so
// request state survives tab switches, case switches, page reloads, and
// browser close. On resume: complete requests show their answer/result;
// pending requests re-establish wait-for-answer polling.
//
// Backend: backend/app/routers/case_requests.py (mounted under /api/v1).
// One row per request:
//   - ai_chat:        one row per chat question (prompt + scope + model_path -> answer).
//   - doc_drop:       one row per dropped FILE uploaded as a regular case document.
//   - doc_reconstruct: one row per dropped FILE reconstructed in place (PII-tagged).
// Status lifecycle: pending -> complete | error.

import apiClient from './client';
import type { ChatScope, ChatModelPath, ChatCitation } from './chat';

export type CaseRequestKind = 'ai_chat' | 'doc_drop' | 'doc_reconstruct';
export type CaseRequestStatus = 'pending' | 'complete' | 'error';

export interface CaseRequestAiChatPayload {
  prompt: string;
  scope: ChatScope;
  model_path: ChatModelPath;
}

export interface CaseRequestDocPayload {
  filename: string;
  size: number;
  content_sha256: string;
}

export interface CaseRequestAiChatResponse {
  answer: string;
  citations?: ChatCitation[];
  path_used?: string | null;
  model_used?: string | null;
  run_id?: string | null;
  docs_excluded_from_cloud?: boolean;
  docs_sent_to_cloud?: boolean;
  pii_method?: string;
}

export interface CaseRequestDocDropResponse {
  doc_ids: string[];
  doc_count: number;
  filename?: string;
}

export interface CaseRequestDocReconstructResponse {
  reconstructed_text: string;
  unknown_tags?: string[];
  warnings?: string[];
  tags_used?: number;
  filename?: string;
}

export interface CaseRequest {
  id: string;
  case_id: string;
  kind: CaseRequestKind;
  status: CaseRequestStatus;
  request_payload: CaseRequestAiChatPayload | CaseRequestDocPayload | Record<string, unknown>;
  response_payload:
    | CaseRequestAiChatResponse
    | CaseRequestDocDropResponse
    | CaseRequestDocReconstructResponse
    | null;
  error: string | null;
  created_at: string | null;
  completed_at: string | null;
}

export interface CaseRequestList {
  case_id: string;
  requests: CaseRequest[];
  pending_count: number;
  total: number;
}

/** Submit an AI chat request. Returns {request_id, status:'pending'} immediately. */
export async function submitAiChat(
  caseId: string,
  body: { prompt: string; scope: ChatScope; model_path: ChatModelPath },
): Promise<{ request_id: string; status: 'pending' }> {
  const { data } = await apiClient.post(`/cases/${caseId}/requests/ai-chat`, body);
  return data;
}

/** Submit a doc drop (multipart). One row per file. Returns the request_ids. */
export async function submitDocDrop(
  caseId: string,
  files: File[],
): Promise<{ request_ids: string[] }> {
  const form = new FormData();
  for (const f of files) form.append('files', f);
  const { data } = await apiClient.post(`/cases/${caseId}/requests/doc-drop`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/** List pending + the `limit` most recent complete rows (oldest-first). */
export async function listCaseRequests(
  caseId: string,
  opts: { include?: string; limit?: number } = {},
): Promise<CaseRequestList> {
  const params = new URLSearchParams();
  params.set('include', opts.include ?? 'pending,recent');
  if (opts.limit != null) params.set('limit', String(opts.limit));
  const { data } = await apiClient.get(`/cases/${caseId}/requests`, { params });
  return data;
}

/** Single request row (for polling). */
export async function getRequest(requestId: string): Promise<CaseRequest> {
  const { data } = await apiClient.get(`/requests/${requestId}`);
  return data;
}

/**
 * Poll a request until it is no longer pending, with backoff + a cap.
 * Interval: 2s initially -> 5s after 60s -> 15s after 5min. Cap 10min.
 * Resolves with the terminal CaseRequest (complete | error). Rejects only
 * on transport error or if the cap is exceeded (status stays 'pending').
 */
export async function pollRequestUntilDone(
  requestId: string,
  onTick?: (r: CaseRequest) => void,
  capMs = 10 * 60 * 1000,
): Promise<CaseRequest> {
  const start = Date.now();
  let delay = 2000;
  for (;;) {
    const r = await getRequest(requestId);
    onTick?.(r);
    if (r.status !== 'pending') return r;
    if (Date.now() - start >= capMs) {
      throw new Error('request timed out (pending after 10min)');
    }
    const elapsed = Date.now() - start;
    delay = elapsed > 5 * 60 * 1000 ? 15000 : elapsed > 60 * 1000 ? 5000 : 2000;
    await new Promise((res) => setTimeout(res, delay));
  }
}